import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';
import { TransactionTypeSelector } from './TransactionTypeSelector';
import { FrequencySelector } from './FrequencySelector';
import { AmountInput } from './AmountInput';
import { TitleInput } from './TitleInput';
import { DateSelector } from './DateSelector';
import { CategorySelector } from './CategorySelector';
import { Category } from '../../types';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { ReminderSelector } from './ReminderSelector';
import { NotificationTimeSelector } from './NotificationTimeSelector';
import { TransactionConfirmation } from './TransactionConfirmation';
import { ProgressBar } from './ProgressBar';
import './AddTransactionFlow.css';

export type TransactionType = 'income' | 'expense';
export type FrequencyType = 'onetime' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface TransactionData {
  type: TransactionType;
  frequency: FrequencyType;
  title: string;
  amount: number;
  date?: Date;
  dayOfWeek?: number; // 0-6 для weekly
  dayOfMonth?: number; // 1-31 для monthly
  dayAndMonth?: { day: number; month: number }; // для yearly
  category?: string;
  payment_method_id?: string;
  source_type?: 'account' | 'goal';
  source_goal_id?: string;
  reminderTimes?: string[]; // Массив времен напоминаний (HH:MM)
  reminderOffsets?: number[]; // Массив смещений (дней до события: 0, 1, 3, 7, 10)
  remindersEnabled?: boolean; // Тогл для напоминаний
  notificationTime?: string; // Время уведомления о автоматическом выполнении (HH:MM)
  description?: string; // Описание транзакции
  receipt_items?: Array<{ // Список товаров из чека
    name: string;
    amount: number;
    quantity?: number;
    unit_price?: number;
  }>;
  currency?: string; // Валюта для отображения списка товаров
}

interface AddTransactionFlowProps {
  onClose: () => void;
  onSubmit: (data: TransactionData) => Promise<void>;
  isPremium: boolean;
  currency?: string;
  recurringOnly?: boolean; // Скрыть опцию "Разовая"
  currencyCode?: string;
  initialType?: TransactionType; // Предвыбранный тип транзакции
  initialData?: Partial<TransactionData>; // Предзаполненные данные (для голосового ввода/чеков)
  categories?: Category[]; // Список категорий
  onSkip?: () => void; // Для пропуска чека в bulk upload
  showSkip?: boolean; // Показывать ли кнопку пропуска
  recurringOnly?: boolean; // Скрыть опцию "Одноразовая" в выборе частоты
  queueProgress?: { current: number; total: number }; // Прогресс обработки очереди (голосовые транзакции/чеки)
}

type Step = 'type' | 'frequency' | 'title' | 'amount' | 'date' | 'category' | 'payment_method' | 'reminders' | 'notification' | 'confirm';

export const AddTransactionFlow: React.FC<AddTransactionFlowProps> = ({ onClose, onSubmit, isPremium, currency = 'RUB', currencyCode, initialType, initialData, categories = [], onSkip, showSkip = false, recurringOnly = false, queueProgress }) => {
  const { t } = useTranslation();
  const finalCurrency = currencyCode || currency;
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  
  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);
  
  // Загрузка payment methods для определения ID по имени
  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const response = await apiClient.getPaymentMethods();
        setPaymentMethods(response.payment_methods || []);
      } catch (error) {
        console.error('Error loading payment methods:', error);
      }
    };
    loadPaymentMethods();
  }, []);
  
  // Формируем начальные данные
  const getInitialData = () => {
    const base: Partial<TransactionData> = {};
    if (initialType) base.type = initialType;
    if (initialData) {
      const result = { ...base, ...initialData };
      
      // Если есть payment_method_name, ищем ID
      if (initialData.payment_method_name && !initialData.payment_method_id) {
        const method = paymentMethods.find(
          m => m.name.toLowerCase() === initialData.payment_method_name.toLowerCase()
        );
        if (method) {
          result.payment_method_id = method.id.toString();
          console.log(`✅ Found payment method: ${method.name} (ID: ${method.id})`);
        }
      }
      
      return result;
    }
    return base;
  };
  
  // Если переданы initialData, показываем экран подтверждения
  const getInitialStep = () => {
    if (initialData && Object.keys(initialData).length > 0) {
      return 'confirm';
    }
    return initialType ? 'frequency' : 'type';
  };
  
  const [step, setStep] = useState<Step>(getInitialStep());
  const [data, setData] = useState<Partial<TransactionData>>(getInitialData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingFromConfirm, setIsEditingFromConfirm] = useState(false);

  // Обновляем данные при изменении paymentMethods
  useEffect(() => {
    if (paymentMethods.length > 0 && initialData?.payment_method_name && !data.payment_method_id) {
      const method = paymentMethods.find(
        m => m.name.toLowerCase() === initialData.payment_method_name.toLowerCase()
      );
      if (method) {
        setData(prev => ({ ...prev, payment_method_id: method.id.toString() }));
        console.log(`✅ Auto-selected payment method: ${method.name} (ID: ${method.id})`);
      }
    }
  }, [paymentMethods, initialData, data.payment_method_id]);

  // Определяем порядок и количество шагов
  const getSteps = (): Step[] => {
    // Если тип предвыбран, пропускаем шаг 'type'
    const baseSteps: Step[] = initialType ? ['frequency', 'title', 'amount'] : ['type', 'frequency', 'title', 'amount'];
    
    // Добавляем выбор даты для всех типов частоты
    if (data.frequency) {
      baseSteps.push('date');
    }
    
    // Время уведомления о автоматическом выполнении (сразу после даты для регулярных)
    if (data.frequency && data.frequency !== 'onetime') {
      baseSteps.push('notification');
    }
    
    // Категория только для расходов
    if (data.type === 'expense') {
      baseSteps.push('category');
    }
    
    // Выбор счета (для всех типов транзакций)
    baseSteps.push('payment_method');
    
    // Напоминания для регулярных транзакций (после счета)
    if (data.frequency && data.frequency !== 'onetime') {
      baseSteps.push('reminders');
    }
    
    baseSteps.push('confirm');
    return baseSteps;
  };

  const steps = useMemo(() => getSteps(), [data.type, data.frequency]);
  const currentStepIndex = steps.indexOf(step);
  const progress = steps.length > 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0;

  const updateData = (updates: Partial<TransactionData>) => {
    console.log('🔄 [AddTransactionFlow] updateData called with:', updates);
    setData(prev => {
      const newData = { ...prev, ...updates };
      console.log('🔄 [AddTransactionFlow] New data state:', {
        reminderTimes: newData.reminderTimes,
        reminderOffsets: newData.reminderOffsets,
        remindersEnabled: newData.remindersEnabled
      });
      return newData;
    });
  };

  const goToNextStep = () => {
    // Если редактируем с экрана подтверждения, возвращаемся к подтверждению
    if (isEditingFromConfirm) {
      setIsEditingFromConfirm(false);
      setStep('confirm');
      return;
    }
    
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setStep(steps[nextIndex]);
    }
  };

  const goToPreviousStep = () => {
    // Если редактируем с экрана подтверждения и нажали "Назад", возвращаемся к подтверждению
    if (isEditingFromConfirm) {
      setIsEditingFromConfirm(false);
      setStep('confirm');
      return;
    }
    
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(steps[prevIndex]);
    } else {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!isDataComplete()) return;

    console.log('📤 [AddTransactionFlow] Submitting transaction data:', {
      type: data.type,
      frequency: data.frequency,
      reminderTimes: data.reminderTimes,
      reminderOffsets: data.reminderOffsets,
      remindersEnabled: data.remindersEnabled,
      fullData: data
    });

    try {
      setIsSubmitting(true);
      await onSubmit(data as TransactionData);
    } catch (error: any) {
      console.error('Error submitting transaction:', error);
      alert(`${t('common.error')}: ${error.message || t('operations.addError')}`);
      throw error; // Передаем ошибку выше для дополнительной обработки
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDataComplete = (): boolean => {
    return !!(
      data.type &&
      data.frequency &&
      data.title &&
      data.amount &&
      (data.type === 'income' || data.category)
    );
  };

  const renderStep = () => {
    switch (step) {
      case 'type':
        return (
          <TransactionTypeSelector
            value={data.type}
            onChange={(type) => {
              updateData({ type });
              goToNextStep();
            }}
          />
        );

      case 'frequency':
        return (
          <FrequencySelector
            transactionType={data.type!}
            value={data.frequency}
            isPremium={isPremium}
            onChange={(frequency) => {
              updateData({ frequency });
              goToNextStep();
            }}
            onBack={goToPreviousStep}
            recurringOnly={recurringOnly}
          />
        );

      case 'title':
        return (
          <TitleInput
            value={data.title || ''}
            transactionType={data.type!}
            frequency={data.frequency!}
            onChange={(title) => updateData({ title })}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );

      case 'amount':
        return (
          <AmountInput
            value={data.amount || 0}
            onChange={(amount) => updateData({ amount })}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            currency={finalCurrency}
          />
        );

      case 'date':
        return (
          <DateSelector
            frequency={data.frequency!}
            value={data.date}
            dayOfWeek={data.dayOfWeek}
            dayOfMonth={data.dayOfMonth}
            dayAndMonth={data.dayAndMonth}
            onChange={(dateData) => {
              updateData(dateData);
              goToNextStep();
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'category':
        console.log('🏷️ [AddTransactionFlow] Rendering CategorySelector with categories:', {
          count: categories.length,
          sample: categories.slice(0, 3)
        });
        return (
          <CategorySelector
            value={data.category}
            title={data.title || ''}
            amount={data.amount || 0}
            isPremium={isPremium}
            categories={categories}
            onChange={(category) => {
              updateData({ category });
              goToNextStep();
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'payment_method':
        return (
          <PaymentMethodSelector
            value={data.payment_method_id}
            sourceType={data.source_type}
            sourceGoalId={data.source_goal_id}
            transactionType={data.type!}
            onChange={(payment_method_id, source_type, source_goal_id) => {
              updateData({ 
                payment_method_id, 
                source_type,
                source_goal_id
              });
              goToNextStep();
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'reminders':
        return (
          <ReminderSelector
            reminderTimes={data.reminderTimes}
            reminderOffsets={data.reminderOffsets}
            remindersEnabled={data.remindersEnabled}
            transactionType={data.type}
            onChange={(reminderData) => {
              updateData(reminderData);
              goToNextStep();
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'notification':
        return (
          <NotificationTimeSelector
            notificationTime={data.notificationTime}
            transactionType={data.type}
            onChange={(notificationData) => {
              updateData(notificationData);
              goToNextStep();
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'confirm':
        return (
          <TransactionConfirmation
            data={data as TransactionData}
            onConfirm={handleSubmit}
            onEdit={(editStep) => {
              setIsEditingFromConfirm(true);
              setStep(editStep);
            }}
            onBack={goToPreviousStep}
            isSubmitting={isSubmitting}
            currency={finalCurrency}
            onSkip={onSkip}
            showSkip={showSkip}
            onUpdateData={updateData}
          />
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div className="add-transaction-flow">
      <div className="add-transaction-flow__overlay" onClick={onClose} />
      <div className="add-transaction-flow__container">
        {step !== 'confirm' && (
          <div className="add-transaction-flow__header">
            <ProgressBar
              progress={progress}
              currentStep={currentStepIndex + 1}
              totalSteps={steps.length}
              queueProgress={queueProgress}
            />
          </div>
        )}
        <div className="add-transaction-flow__content">
          {renderStep()}
        </div>
      </div>
    </div>,
    document.body
  );
};

